import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MtxTooltipModule } from '@ng-matero/extensions/tooltip';
import { RouterModule } from '@angular/router';
import { ConfirmDialogueComponent } from '../components/confirm-dialogue/confirm-dialogue.component';
import { SortUtilService } from '../components/sort-util.service';
import { ConfigService } from '../services/config.service';
import { GlobalService } from '../../../../../my-shared-lib/src/lib/shared-services/global.service';


type ScoreField =
  | 'TCO'
  | 'affordability'
  | 'availability'
  | 'productivity'
  | 'sustainability';

@Component({
  selector: 'app-audit',
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    CommonModule,
    MatIconModule,
    FormsModule,
    NgxMatSelectSearchModule,
    MatTableModule,
    MatTooltipModule,
    MtxTooltipModule,
    RouterModule
  ],
  templateUrl: './audit.component.html',
  styleUrl: './audit.component.scss',
})
export class AuditComponent implements OnChanges {
  @Input() data: any;
  @Input() embedded = false;
  @Input() showFilters = true;
  @Input() showLegend = true;
  @Input() showActions = true;

  filteredEngines = [];
  constructor(
    private cdr: ChangeDetectorRef,
    private service: ConfigService,
    private sortUtil: SortUtilService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private globalService: GlobalService
  ) { }
  market = [];
  engine: any = [];
  selectedMarket: any = [];
  selectedEngine: any = [];
  displayedColumns: string[] = [
    'product',
    'keycompetitor',
    'user',
    'tco',
    'affordability',
    'availability',
    'sustainability',
    'productivity',
    'notes',
    'delete',
  ];
  dataSource: any = [];
  averageValues = {
    TCO: 0,
    affordability: 0,
    availability: 0,
    productivity: 0,
    sustainability: 0,
  };
  def: any;
  ngOnInit() {
    if (this.data) {
      this.applyConfiguredData();
    }

    this.globalService.getMarketSeg().subscribe((res: any) => {
      if (this.data) return;
      this.market = res?.responseList?.[0]?.responseData?.[0].segments;
      this.selectedMarket = this.market[0];
      this.getEngine();
    });
    this.service.getDef().subscribe((def) => {
      this.def = def;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.applyConfiguredData();
    }
  }

  private applyConfiguredData(): void {
    this.market = this.data?.markets || [];
    this.engine = this.data?.engines || [];
    this.filteredEngines = this.engine;
    this.selectedMarket = this.data?.selectedMarket || this.market?.[0] || '';
    this.selectedEngine = this.data?.selectedEngine || this.engine?.[0] || '';
    this.displayedColumns = this.data?.displayedColumns || this.displayedColumns;
    this.dataSource = (this.data?.rows || []).map((row: any) => ({ ...row }));
    this.calculateAverages();
  }

  getEngine() {
    if (this.data) {
      this.applyConfiguredData();
      return;
    }
    this.globalService
      .getEngineModel([this.selectedMarket])
      .subscribe((engineModel: any) => {
        engineModel = engineModel.responseList?.[0]?.responseData?.[0].engineModels;
        this.engine = this.sortUtil.sortByFirstNumber(engineModel);
        this.filteredEngines = this.engine;
        if (
          !this.selectedEngine ||
          !engineModel.includes(this.selectedEngine)
        ) {
          this.selectedEngine = engineModel[0];
        }
        this.cdr.detectChanges();
        this.getData();
      });
  }
  getData() {
    if (this.data) {
      this.applyConfiguredData();
      return;
    }
    this.service
      .readApi(this.selectedMarket, this.selectedEngine)
      .subscribe((res: any) => {
        this.dataSource =
          res?.responseList?.[0]?.survey_history[0].userResponses;
        this.service
          .getChartRowData([this.selectedMarket], [this.selectedEngine])
          .subscribe((productIds: any) => {
            productIds = productIds?.responseList?.[0]?.responseData?.[0];
            const allProducts: any[] = [];

            ['q1', 'q2', 'q3', 'q4'].forEach((quarter) => {
              if (productIds[quarter]) {
                allProducts.push(...productIds[quarter]);
              }
            });
            const product = this.selectedEngine + ' ' + this.selectedMarket;
            const match = allProducts.find((p) => p.name === product);
            this.dataSource.forEach((sourceItem: any) => {
              sourceItem.keyCompetitor = match.keyCompetitor;
            });

            this.calculateAverages();
          });
      });
    this.cdr.detectChanges();
  }
  calculateAverages() {
    const fields: ScoreField[] = [
      'TCO',
      'affordability',
      'availability',
      'productivity',
      'sustainability',
    ];

    fields.forEach((field) => {
      let sum = 0;
      let count = 0;

      this.dataSource.forEach((row: any) => {
        const isNA = row.isNA?.[field];
        const value = Number(row[field]);

        if (!isNA && value > 0) {
          sum += value;
          count++;
        }
      });

      this.averageValues[field] = count > 0 ? sum / count : 0;
    });
  }

  resetSelection() {
    this.getEngine();
  }

  onEngineChange() {
    this.getData();
  }

  filterEngineSelection(text: string) {
    this.filteredEngines = this.engine.filter((item: any) => {
      return item.trim().toLowerCase().indexOf(text.trim().toLowerCase()) != -1;
    });
  }

  async deleteRow(index: number, element: any): Promise<void> {
    if (this.data) {
      this.dataSource.splice(index, 1);
      this.dataSource = [...this.dataSource];
      this.calculateAverages();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogueComponent, {
      width: '400px',
      data: { title: 'Delete', desc: 'The row will be deleted' },
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;
    this.service.deleteRow(element).subscribe({
      next: (res) => {
        this.dataSource.splice(index, 1);
        this.dataSource = [...this.dataSource];
        this.calculateAverages();
        this.snackBar.open('Deleted Successfully', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-success'],
        });
      },
      error: (err) => {
        this.snackBar.open('Failed to delete.', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-success'],
        });
      },
    });
  }
  save() {
    if (this.data) {
      return;
    }

    const payload = this.dataSource.map((row: any) => ({
      product_id: row.product,
      email: row.email,
      TCO: row.TCO,
      affordability: row.affordability,
      availability: row.availability,
      productivity: row.productivity,
      sustainability: row.sustainability,
      keycompetitor: row.keycompetitor,
      notes: row.notes,
    }));
    this.service.saveApi(payload).subscribe({
      next: (res) => {
        this.snackBar.open('Survey saved successfully!', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-success'],
        });
      },
      error: (err) => {
        this.snackBar.open('Failed to save survey.', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-error'],
        });
      },
    });
  }
  getToolTip(name: string) {
    if (this.def) return this.def[name] || 'No definition available';
  }
}
